const PROXY_URL = '/api/fitness-ai';

async function callClaude(prompt, maxTokens = 1000) {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, maxTokens }),
  });

  if (response.ok) {
    const data = await response.json();
    return data.text;
  }

  const errBody = await response.json().catch(() => ({}));

  // Dev-only fallback when Vite middleware isn't running (never in production builds)
  if (!import.meta.env.PROD && import.meta.env.VITE_ANTHROPIC_API_KEY) {
    const direct = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: maxTokens,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (direct.ok) {
      const data = await direct.json();
      return data.content.map((b) => b.text || '').join('');
    }
  }

  throw new Error(errBody.error || `Claude API error: ${response.status}`);
}

function parseJSON(text) {
  try {
    return JSON.parse(text.replace(/```json|```/g, '').trim());
  } catch {
    const match = text.match(/\{[\s\S]*\}/);
    if (match) return JSON.parse(match[0]);
    throw new Error('Could not parse AI response as JSON');
  }
}

export async function getCoachInsight({ profile, recentWorkouts, screen }) {
  const text = await callClaude(`
You are an elite fitness coach — think the best S&C coach at the Arnold Classic level.
Athlete profile: weight ${profile.weight}kg, height ${profile.height}cm, age ${profile.age}, level: ${profile.fitnessLevel}, goals: ${profile.goals?.join(', ')}.
Recent workouts (last 5): ${JSON.stringify(recentWorkouts?.slice(0, 5) || [])}.
Current screen: ${screen}.

Give ONE short motivating coaching insight (max 2 sentences, direct, no fluff).
Then ONE specific action for today.
Pick a relevant emoji.

Respond ONLY in this JSON, no other text:
{"insight":"...","action":"...","emoji":"..."}
`);
  return parseJSON(text);
}

export async function generateWorkoutPlan({ profile, goal, daysPerWeek, equipment, category }) {
  const text = await callClaude(`
Create a ${daysPerWeek}-day workout plan.
Athlete: ${profile.weight}kg, ${profile.height}cm, ${profile.age}yo, ${profile.fitnessLevel} level.
Goal: ${goal}. Category: ${category}. Equipment: ${equipment?.join(', ') || 'full gym'}.

Include progressive overload. Each exercise must have name, sets, reps/duration, rest, coaching note.

Respond ONLY in this exact JSON, no other text:
{
  "planName": "string",
  "planDescription": "string",
  "estimatedWeeklyCalories": 0,
  "weeklyStructure": [
    {
      "day": 1,
      "dayName": "Monday",
      "focus": "string",
      "estimatedDuration": 60,
      "warmup": "string",
      "exercises": [
        {"name":"string","sets":3,"reps":"8-12","rest":"90s","equipment":"string","coachingNote":"string"}
      ],
      "cooldown": "string"
    }
  ],
  "progressionNotes": "string",
  "nutritionTips": ["string"]
}
`, 1500);
  return parseJSON(text);
}

export async function generateSportPlan({ sport, profile }) {
  const text = await callClaude(`
You are an elite S&C coach who trains ${sport.name} athletes at international level.

Athlete: ${profile.weight}kg, ${profile.height}cm, ${profile.age}yo, ${profile.fitnessLevel} level.
Sport: ${sport.name}. Energy system: ${sport.energySystem}.
Primary muscles: ${sport.primaryMuscles?.join(', ')}.
Key movements: ${sport.keyLifts?.join(', ')}.

Create a 4-week periodised mesocycle. Week 1 = accumulation, Week 2 = intensification, Week 3 = realisation, Week 4 = deload.

Respond ONLY in this JSON, no other text:
{
  "blockName": "string",
  "blockGoal": "string",
  "weeks": [
    {
      "week": 1,
      "theme": "string",
      "totalVolume": "string",
      "days": [
        {
          "day": "Monday",
          "sessionType": "string",
          "duration": 60,
          "rpe": "7-8",
          "exercises": [
            {"name":"string","sets":4,"reps":"string","intensity":"string","rest":"string","notes":"string"}
          ]
        }
      ]
    }
  ],
  "keyPerformanceIndicators": ["string"],
  "nutritionFocus": "string",
  "recoveryProtocol": "string",
  "injuryPrevention": ["string"]
}
`, 1500);
  return parseJSON(text);
}

export async function generateMealPlan({ profile, goal, restrictions, tdee }) {
  const text = await callClaude(`
Create a 7-day meal plan.
Athlete: ${profile.weight}kg, ${profile.age}yo. TDEE: ${tdee} kcal. Goal: ${goal}.
Dietary restrictions: ${restrictions?.join(', ') || 'none'}.

Make meals practical, performance-focused, and delicious. Include macros for each meal.

Respond ONLY in this JSON, no other text:
{
  "totalDailyCalories": 0,
  "totalProtein": 0,
  "totalCarbs": 0,
  "totalFat": 0,
  "days": [
    {
      "day": "Monday",
      "meals": [
        {"mealName":"Breakfast","foods":["string"],"calories":0,"protein":0,"carbs":0,"fat":0,"prepTime":"5 min"}
      ]
    }
  ],
  "shoppingList": ["string"],
  "supplementStack": ["string"],
  "timingTips": ["string"]
}
`, 1500);
  return parseJSON(text);
}

export async function analyzeProgress({ workoutHistory, bodyStats, profile }) {
  const text = await callClaude(`
Analyze this athlete's last 30 days of training.
Profile: ${profile.weight}kg, ${profile.fitnessLevel} level, goals: ${profile.goals?.join(', ')}.
Workout history (last 20): ${JSON.stringify(workoutHistory?.slice(0, 20) || [])}.
Body stats: ${JSON.stringify(bodyStats || {})}.

Be direct, specific, and actionable like an elite coach.

Respond ONLY in this JSON, no other text:
{
  "overallScore": 85,
  "summary": "string",
  "strengths": ["string"],
  "weaknesses": ["string"],
  "recommendations": ["string"],
  "predictedOutcome": "string",
  "adjustments": {"volume":"string","intensity":"string","recovery":"string","nutrition":"string"}
}
`, 1200);
  return parseJSON(text);
}
